/**
 * Delivery — kanban de bugs + pedidos de feature.
 *
 * Qualquer usuário do admin abre um item (com print de tela opcional, colando,
 * arrastando ou escolhendo um arquivo) e o time da EuNerd prioriza/move pelas
 * colunas, construindo conforme a demanda.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bug,
  ImageIcon,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import {
  useMemo,
  useState,
  type ClipboardEvent,
  type DragEvent,
} from "react";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  DELIVERY_KIND_LABEL,
  DELIVERY_PRIORITY,
  DELIVERY_PRIORITY_LABEL,
  DELIVERY_STATUS,
  deliveryApi,
  type DeliveryItem,
  type DeliveryKind,
  type DeliveryPriority,
  type DeliveryStatus,
} from "@/lib/api-delivery";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

const PRIORITY_VARIANT: Record<DeliveryPriority, string> = {
  low: "muted",
  medium: "info",
  high: "warning",
  urgent: "danger",
};

const COLUMN_ACCENT: Record<DeliveryStatus, string> = {
  backlog: "bg-slate-400",
  analyzing: "bg-amber-400",
  planned: "bg-sky-400",
  in_progress: "bg-violet-400",
  done: "bg-emerald-400",
  rejected: "bg-rose-400",
};

const KIND_FILTERS: { v: DeliveryKind | "all"; label: string }[] = [
  { v: "all", label: "Todos" },
  { v: "bug", label: "Bugs" },
  { v: "feature", label: "Features" },
];

function StatChip({
  label,
  value,
  tone = "muted",
}: {
  label: string;
  value: number;
  tone?: "muted" | "danger" | "primary" | "warning" | "success";
}) {
  const toneCls: Record<string, string> = {
    muted: "bg-slate-100 text-slate-600 dark:bg-white/5 dark:text-slate-300",
    danger: "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300",
    primary: "bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-300",
    warning: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300",
    success: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium",
        toneCls[tone],
      )}
    >
      <span className="tabular-nums">{value}</span> {label}
    </span>
  );
}

function KindBadge({ kind }: { kind: DeliveryKind }) {
  return (
    <Badge variant={(kind === "bug" ? "danger" : "primary") as never}>
      {kind === "bug" ? (
        <Bug className="h-3 w-3" />
      ) : (
        <Sparkles className="h-3 w-3" />
      )}
      {DELIVERY_KIND_LABEL[kind]}
    </Badge>
  );
}

/** Extrai a primeira imagem de um evento de paste/drop. */
function imageFromTransfer(items: DataTransferItemList | null): File | null {
  if (!items) return null;
  for (const it of Array.from(items)) {
    if (it.kind === "file" && it.type.startsWith("image/")) {
      const f = it.getAsFile();
      if (f) return f;
    }
  }
  return null;
}

export function DeliveryPage() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [kindFilter, setKindFilter] = useState<DeliveryKind | "all">("all");

  const list = useQuery({
    queryKey: ["delivery"],
    queryFn: () => deliveryApi.list({ size: 500 }),
  });

  const update = useMutation({
    mutationFn: ({ id, status }: { id: string; status: DeliveryStatus }) =>
      deliveryApi.update(id, { status }),
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: ["delivery"] });
      const prev = qc.getQueryData<{ items: DeliveryItem[] }>(["delivery"]);
      if (prev) {
        qc.setQueryData(["delivery"], {
          ...prev,
          items: prev.items.map((i) => (i.id === id ? { ...i, status } : i)),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["delivery"], ctx.prev);
      toast.error("Falha ao mover o card");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["delivery"] }),
  });

  const grouped = useMemo(() => {
    const map = new Map<DeliveryStatus, DeliveryItem[]>();
    for (const s of DELIVERY_STATUS) map.set(s.value, []);
    for (const it of list.data?.items ?? []) {
      if (kindFilter !== "all" && it.kind !== kindFilter) continue;
      map.get(it.status as DeliveryStatus)?.push(it);
    }
    return map;
  }, [list.data, kindFilter]);

  const stats = useMemo(() => {
    const items = list.data?.items ?? [];
    const closed = new Set<DeliveryStatus>(["done", "rejected"]);
    const openItems = items.filter((i) => !closed.has(i.status as DeliveryStatus));
    return {
      open: openItems.length,
      bugs: items.filter((i) => i.kind === "bug").length,
      features: items.filter((i) => i.kind === "feature").length,
      urgent: openItems.filter((i) => i.priority === "urgent").length,
      done: items.filter((i) => i.status === "done").length,
    };
  }, [list.data]);

  return (
    <>
      <PageHeader
        title="Delivery"
        description="Bugs e pedidos de feature — registre, priorize e acompanhe a entrega"
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Novo pedido
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-xl border border-slate-200/70 bg-white p-0.5 dark:border-white/10 dark:bg-card/60">
          {KIND_FILTERS.map((opt) => (
            <button
              key={opt.v}
              onClick={() => setKindFilter(opt.v)}
              className={cn(
                "rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                kindFilter === opt.v
                  ? "bg-primary-600 text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <StatChip label="em aberto" value={stats.open} />
          <StatChip label="bugs" value={stats.bugs} tone="danger" />
          <StatChip label="features" value={stats.features} tone="primary" />
          {stats.urgent > 0 && <StatChip label="urgentes" value={stats.urgent} tone="warning" />}
          {stats.done > 0 && <StatChip label="entregues" value={stats.done} tone="success" />}
        </div>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4 [scrollbar-width:thin]">
        {DELIVERY_STATUS.map((col) => {
          const items = grouped.get(col.value) ?? [];
          return (
            <div
              key={col.value}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData("text/plain");
                if (id) update.mutate({ id, status: col.value });
              }}
              className="flex w-72 shrink-0 flex-col rounded-2xl border border-slate-200/60 bg-slate-50/50 dark:border-white/5 dark:bg-white/[0.02]"
            >
              <div className="flex items-center gap-2 px-3 py-2.5">
                <span className={cn("h-2 w-2 rounded-full", COLUMN_ACCENT[col.value])} />
                <span className="text-sm font-semibold">{col.label}</span>
                <span className="ml-auto rounded-full bg-slate-200/70 px-2 text-xs tabular-nums text-muted-foreground dark:bg-white/10">
                  {items.length}
                </span>
              </div>
              <div className="flex min-h-[120px] flex-col gap-2 px-2 pb-2">
                {list.isLoading ? (
                  <p className="px-2 py-4 text-xs text-muted-foreground">Carregando…</p>
                ) : items.length === 0 ? (
                  <p className="px-2 py-4 text-center text-xs text-muted-foreground/60">
                    Arraste cards aqui
                  </p>
                ) : (
                  items.map((it) => (
                    <button
                      key={it.id}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("text/plain", it.id)}
                      onClick={() => setOpenId(it.id)}
                      className="group cursor-grab rounded-xl border border-slate-200/70 bg-white p-3 text-left shadow-sm transition-all hover:shadow-md active:cursor-grabbing dark:border-white/10 dark:bg-card/60"
                    >
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <KindBadge kind={it.kind} />
                        <Badge variant={(PRIORITY_VARIANT[it.priority] ?? "muted") as never} dot>
                          {DELIVERY_PRIORITY_LABEL[it.priority]}
                        </Badge>
                        {it.has_screenshot && (
                          <ImageIcon className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
                        )}
                      </div>
                      <p className="line-clamp-2 text-sm font-medium">{it.title}</p>
                      <p className="mt-1 truncate text-[11px] text-muted-foreground">
                        {it.reporter_name ?? it.reporter_email ?? "—"} ·{" "}
                        {formatRelative(it.created_at)}
                      </p>
                    </button>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      <CreateDialog open={creating} onClose={() => setCreating(false)} />
      <DetailSheet openId={openId} onClose={() => setOpenId(null)} />
    </>
  );
}

// ─── Criar ──────────────────────────────────────────────────────────────────
function CreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<DeliveryKind>("feature");
  const [priority, setPriority] = useState<DeliveryPriority>("medium");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [pageUrl, setPageUrl] = useState("");
  const [shotPath, setShotPath] = useState<string | null>(null);
  const [shotPreview, setShotPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const reset = () => {
    setKind("feature");
    setPriority("medium");
    setTitle("");
    setDescription("");
    setPageUrl("");
    setShotPath(null);
    setShotPreview(null);
    setUploading(false);
  };

  const handleImage = async (file: File) => {
    setUploading(true);
    setShotPreview(URL.createObjectURL(file));
    try {
      const ext = (file.type.split("/")[1] || "png").replace("jpeg", "jpg");
      const ct = file.type || "image/png";
      const { upload_url, storage_path } = await deliveryApi.uploadUrl(
        `screenshot.${ext}`,
        ct,
      );
      if (!upload_url.startsWith("https://localhost")) {
        const r = await fetch(upload_url, {
          method: "PUT",
          headers: { "Content-Type": ct },
          body: file,
        });
        if (!r.ok) throw new Error(`upload ${r.status}`);
      }
      setShotPath(storage_path);
      toast.success("Print anexado");
    } catch (e) {
      toast.error("Falha ao anexar print", {
        description: e instanceof Error ? e.message : undefined,
      });
      setShotPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const onPaste = (e: ClipboardEvent) => {
    const f = imageFromTransfer(e.clipboardData.items);
    if (f) {
      e.preventDefault();
      void handleImage(f);
    }
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    const f = imageFromTransfer(e.dataTransfer.items);
    if (f) void handleImage(f);
  };

  const create = useMutation({
    mutationFn: () =>
      deliveryApi.create({
        kind,
        priority,
        title: title.trim(),
        description: description.trim() || null,
        page_url: pageUrl.trim() || null,
        screenshot_path: shotPath,
      }),
    onSuccess: () => {
      toast.success("Pedido criado");
      qc.invalidateQueries({ queryKey: ["delivery"] });
      reset();
      onClose();
    },
    onError: () => toast.error("Falha ao criar pedido"),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-xl" onPaste={onPaste}>
        <DialogHeader>
          <DialogTitle>Novo pedido</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as DeliveryKind)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="feature">Feature</SelectItem>
                  <SelectItem value="bug">Bug</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Prioridade</Label>
              <Select
                value={priority}
                onValueChange={(v) => setPriority(v as DeliveryPriority)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DELIVERY_PRIORITY.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="d-title">Título</Label>
            <Input
              id="d-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex.: Botão de exportar não funciona em Faturas"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="d-desc">Descrição</Label>
            <Textarea
              id="d-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="O que aconteceu / o que você gostaria. Passos pra reproduzir, se for bug."
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="d-url">Página/URL (opcional)</Label>
            <Input
              id="d-url"
              value={pageUrl}
              onChange={(e) => setPageUrl(e.target.value)}
              placeholder="/invoices, /jobs/123…"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Print da tela</Label>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              onClick={() => document.getElementById("d-shot-input")?.click()}
              className="cursor-pointer rounded-xl border-2 border-dashed border-slate-300 p-4 text-center transition-colors hover:border-slate-400 dark:border-white/10 dark:hover:border-white/20"
            >
              <input
                id="d-shot-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleImage(f);
                  e.target.value = "";
                }}
              />
              {uploading ? (
                <div className="flex flex-col items-center gap-1 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <p className="text-xs">Enviando…</p>
                </div>
              ) : shotPreview ? (
                <img
                  src={shotPreview}
                  alt="print"
                  className="mx-auto max-h-40 rounded-lg border border-slate-200 dark:border-white/10"
                />
              ) : (
                <div className="flex flex-col items-center gap-1 text-muted-foreground">
                  <Upload className="h-5 w-5" />
                  <p className="text-xs font-medium">
                    Cole (Ctrl/Cmd+V), arraste ou clique pra escolher
                  </p>
                  <p className="text-[11px] text-muted-foreground/70">
                    Tire o print (Cmd+Ctrl+Shift+4 no Mac) e cole aqui
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => create.mutate()}
            disabled={!title.trim() || uploading || create.isPending}
          >
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Criar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Detalhe ──────────────────────────────────────────────────────────────
function DetailSheet({ openId, onClose }: { openId: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);

  const detail = useQuery({
    queryKey: ["delivery", openId],
    queryFn: () => deliveryApi.get(openId!),
    enabled: !!openId,
  });
  const d = detail.data;

  const refetch = () => {
    qc.invalidateQueries({ queryKey: ["delivery"] });
    qc.invalidateQueries({ queryKey: ["delivery", openId] });
  };

  const patch = useMutation({
    mutationFn: (body: Parameters<typeof deliveryApi.update>[1]) =>
      deliveryApi.update(openId!, body),
    onSuccess: () => {
      refetch();
      setNotesDirty(false);
      toast.success("Atualizado");
    },
    onError: () => toast.error("Falha ao atualizar"),
  });

  const remove = useMutation({
    mutationFn: () => deliveryApi.remove(openId!),
    onSuccess: () => {
      toast.success("Removido");
      qc.invalidateQueries({ queryKey: ["delivery"] });
      onClose();
    },
  });

  return (
    <Sheet open={!!openId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" size="lg">
        <SheetHeader>
          <SheetTitle>{d?.title ?? "Pedido"}</SheetTitle>
        </SheetHeader>
        <SheetBody>
          {detail.isLoading || !d ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-1.5">
                <KindBadge kind={d.kind} />
                <Badge variant={(PRIORITY_VARIANT[d.priority] ?? "muted") as never} dot>
                  {DELIVERY_PRIORITY_LABEL[d.priority]}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Coluna</Label>
                  <Select
                    value={d.status}
                    onValueChange={(v) => patch.mutate({ status: v as DeliveryStatus })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DELIVERY_STATUS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Prioridade</Label>
                  <Select
                    value={d.priority}
                    onValueChange={(v) => patch.mutate({ priority: v as DeliveryPriority })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DELIVERY_PRIORITY.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {d.description && (
                <div className="space-y-1">
                  <Label>Descrição</Label>
                  <p className="whitespace-pre-wrap rounded-lg border bg-slate-50/50 p-3 text-sm dark:bg-white/[0.02]">
                    {d.description}
                  </p>
                </div>
              )}

              {d.page_url && (
                <p className="text-xs text-muted-foreground">
                  Página: <span className="font-mono">{d.page_url}</span>
                </p>
              )}

              {d.screenshot_url && (
                <div className="space-y-1">
                  <Label>Print</Label>
                  <a href={d.screenshot_url} target="_blank" rel="noreferrer">
                    <img
                      src={d.screenshot_url}
                      alt="print"
                      className="w-full rounded-lg border border-slate-200 dark:border-white/10"
                    />
                  </a>
                </div>
              )}

              <div className="space-y-1">
                <Label htmlFor="d-notes">Notas do time</Label>
                <Textarea
                  id="d-notes"
                  rows={3}
                  defaultValue={d.admin_notes ?? ""}
                  onChange={(e) => {
                    setNotes(e.target.value);
                    setNotesDirty(true);
                  }}
                  placeholder="Análise, decisão, encaminhamento…"
                />
                {notesDirty && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => patch.mutate({ admin_notes: notes })}
                    disabled={patch.isPending}
                  >
                    Salvar notas
                  </Button>
                )}
              </div>

              <p className="text-[11px] text-muted-foreground">
                Reportado por {d.reporter_name ?? d.reporter_email ?? "—"} ·{" "}
                {formatRelative(d.created_at)}
              </p>
            </div>
          )}
        </SheetBody>
        {d && (
          <SheetFooter>
            <Button
              variant="ghost"
              size="sm"
              className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              <Trash2 className="h-4 w-4" /> Excluir
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
